import React from "react";

const DURATION_FACTOR_OPTIONS = [
  0.3,
  0.4,
  0.5,
  0.75,
  1,
  1.25,
  1.5,
  2,
  2.5,
  3,
];

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatSpeed(speed) {
  if (speed === 1) return "100%";
  if (speed === 0.5) return "50%";
  if (speed === 0.25) return "25%";
  return `${Math.round(Number(speed) * 100)}%`;
}

function ActionCard({
  children,
  disabled,
  active,
  tone = "slate",
  className,
  style,
  ...props
}) {
  const toneClass =
    tone === "blue"
      ? active
        ? "border-blue-700 bg-blue-600 text-white"
        : "border-blue-200 bg-white text-blue-800 hover:border-blue-300 hover:bg-blue-50"
      : tone === "violet"
        ? active
          ? "border-violet-700 bg-violet-600 text-white"
          : "border-violet-300 bg-white text-violet-800 hover:border-violet-400 hover:bg-violet-50"
        : tone === "green"
          ? active
            ? "border-emerald-700 bg-emerald-600 text-white"
            : "border-emerald-300 bg-white text-emerald-800 hover:border-emerald-400 hover:bg-emerald-50"
          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50";

  return (
    <button
      type="button"
      disabled={disabled}
      className={cx(
        "min-h-[104px] rounded-2xl border-2 px-4 py-3 text-sm font-black shadow-sm transition hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50",
        toneClass,
        className
      )}
      style={style}
      {...props}
    >
      {children}
    </button>
  );
}

export default function BetterMeControlPanel({
  hintText,

  durationFactor,
  onDurationFactorChange,
  onAddDurationRegion,

  localSpeed,
  onSpeedChange,

  playing,
  canInteract,
  canPlay,
  loading,
  savingBetterMe,

  sourceAudioUrl,
  workingAudioUrl,
  previewAudioId,
  handlesCount,
  hasUnsavedTarget,

  onPlayOriginal,
  onPlayEdited,
  onTryPreview,
  onSaveBetterMe,
}) {
  const canMarkDuration =
    canInteract &&
    !loading &&
    !savingBetterMe &&
    Number(durationFactor) !== 1;

  const canPlayOriginal =
    canPlay &&
    !loading &&
    !savingBetterMe &&
    Boolean(sourceAudioUrl);

  const canPlayEdited =
    canPlay &&
    !loading &&
    !savingBetterMe &&
    Boolean(workingAudioUrl);

  const canGenerateBetterMe =
    canInteract &&
    !loading &&
    !savingBetterMe &&
    Number(handlesCount) >= 2;

  const canSaveBetterMe =
    canInteract &&
    !loading &&
    !savingBetterMe &&
    Boolean(previewAudioId);

  const hasEditedAudio = Boolean(workingAudioUrl);
  const hasPreview = Boolean(previewAudioId);

  const generateLabel = loading
    ? "Generating…"
    : hasEditedAudio && hasUnsavedTarget
      ? "✦ Regenerate Better Me"
      : hasEditedAudio
        ? "✦ Regenerate Better Me"
        : "✦ Generate Better Me";

  const generateHint = hasEditedAudio
    ? hasUnsavedTarget
      ? "Edits changed"
      : "Preview ready"
    : "Creates edited audio";

  const saveLabel = savingBetterMe
    ? "Saving…"
    : hasPreview
      ? "✓ Save Better Me"
      : "Save Better Me";

  return (
    <div className="mt-4 rounded-[26px] border border-violet-100 bg-white p-4 shadow-sm">
      <div className="grid gap-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold leading-6 text-slate-700">
          {hintText}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[1.05fr_0.95fr_1fr_1.25fr_1fr_1fr]">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-[10px] font-black uppercase tracking-[0.14em] text-amber-700">
                Duration A–B
              </span>

              <select
                value={durationFactor}
                onChange={(event) =>
                  onDurationFactorChange(Number(event.target.value))
                }
                className="min-w-0 flex-1 rounded-xl border border-amber-200 bg-white px-3 py-2 text-xs font-black text-amber-900 outline-none focus:ring-4 focus:ring-amber-100"
              >
                {DURATION_FACTOR_OPTIONS.map((factor) => (
                  <option key={factor} value={factor}>
                    {factor}×
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={onAddDurationRegion}
              disabled={!canMarkDuration}
              className="mt-2 w-full rounded-xl border border-amber-500 bg-amber-400 px-3 py-2 text-xs font-black text-amber-950 shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Mark duration
            </button>

            <div className="mt-2 text-[11px] font-bold leading-4 text-amber-800">
              A/B selects what plays and what duration edit affects. Less than 1× compresses; more than 1× stretches.
            </div>
          </div>

          <div className="min-h-[104px] rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                Speed
              </span>

              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black text-slate-500">
                {formatSpeed(localSpeed)}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-1 rounded-2xl bg-white p-1.5 shadow-inner">
              {[
                { value: 0.25, label: "25" },
                { value: 0.5, label: "50" },
                { value: 1, label: "100" },
              ].map((speed) => {
                const active = localSpeed === speed.value;

                return (
                  <button
                    key={speed.value}
                    type="button"
                    onClick={() => onSpeedChange(speed.value)}
                    className={cx(
                      "min-h-[54px] rounded-xl text-sm font-black transition focus:outline-none focus:ring-4 focus:ring-blue-100",
                      active
                        ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                        : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                    )}
                  >
                    {speed.label}
                  </button>
                );
              })}
            </div>
          </div>

          <ActionCard
            onClick={onPlayOriginal}
            disabled={!canPlayOriginal}
            active={playing === "original"}
            tone="blue"
            title="Play selected A/B region from the original recording"
          >
            <div className="flex h-full flex-col items-center justify-center gap-1">
              <span>{playing === "original" ? "■ Stop original" : "▶ Play original"}</span>
              <span className="text-[11px] font-black opacity-70">
                Selected A/B
              </span>
            </div>
          </ActionCard>

          <button
            type="button"
            onClick={onTryPreview}
            disabled={!canGenerateBetterMe}
            title="Create edited audio from the current pitch and duration edits."
            className="min-h-[104px] rounded-2xl border-2 px-5 py-3 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: "#6d28d9",
              color: "#ffffff",
              borderColor: "#4c1d95",
              boxShadow: "0 14px 30px rgba(109, 40, 217, 0.32)",
            }}
          >
            <div className="flex h-full flex-col items-center justify-center gap-1">
              <span>{generateLabel}</span>
              <span className="text-[11px] font-black text-violet-100">
                {generateHint}
              </span>
            </div>
          </button>

          <ActionCard
            onClick={onPlayEdited}
            disabled={!canPlayEdited}
            active={playing === "edited"}
            tone="violet"
            title={
              workingAudioUrl
                ? "Play selected A/B region from latest Better Me audio"
                : "Generate Better Me first"
            }
          >
            <div className="flex h-full flex-col items-center justify-center gap-1">
              <span>{playing === "edited" ? "■ Stop edited" : "▶ Play edited"}</span>
              <span className="text-[11px] font-black opacity-70">
                Selected A/B
              </span>
            </div>
          </ActionCard>

          <button
            type="button"
            onClick={onSaveBetterMe}
            disabled={!canSaveBetterMe}
            title={
              previewAudioId
                ? "Attach this Better Me audio to the verdict."
                : "Generate Better Me before saving."
            }
            className="min-h-[104px] rounded-2xl border-2 px-5 py-3 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: previewAudioId ? "#059669" : "#e2e8f0",
              color: previewAudioId ? "#ffffff" : "#64748b",
              borderColor: previewAudioId ? "#047857" : "#cbd5e1",
              boxShadow: previewAudioId
                ? "0 14px 30px rgba(5, 150, 105, 0.28)"
                : "none",
            }}
          >
            <div className="flex h-full flex-col items-center justify-center gap-1">
              <span>{saveLabel}</span>
              <span className="text-[11px] font-black opacity-75">
                {previewAudioId ? "Attach to verdict" : "After preview"}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}